/* ============================================
   ODMPOD — Dark Luxury Theme JavaScript
   Handles: cart drawer, search drawer, mobile menu,
   product variants, header scroll, AJAX cart
   ============================================ */

(function () {
  'use strict';

  /* ---- Utility ---- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));
  const formatMoney = (cents) => {
    return '$' + (cents / 100).toFixed(2);
  };

  /* ---- Header scroll behavior ---- */
  const header = $('.site-header');
  if (header) {
    let lastScroll = 0;
    window.addEventListener('scroll', () => {
      const currentScroll = window.scrollY;
      if (currentScroll > 80) {
        header.classList.add('site-header--scrolled');
      } else {
        header.classList.remove('site-header--scrolled');
      }
      lastScroll = currentScroll;
    }, { passive: true });
  }

  /* ---- Drawer/overlay management ---- */
  function openDrawer(drawerId) {
    const drawer = document.getElementById(drawerId);
    const overlay = $('#overlay');
    if (drawer) {
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      if (overlay) overlay.classList.add('is-visible');
      document.body.style.overflow = 'hidden';
    }
  }

  function closeDrawer(drawerId) {
    const drawer = document.getElementById(drawerId);
    const overlay = $('#overlay');
    if (drawer) {
      drawer.classList.remove('is-open');
      drawer.setAttribute('aria-hidden', 'true');
    }
    // Check if any other drawer is open before removing overlay
    const anyOpen = $('.cart-drawer.is-open, .search-drawer.is-open, .mobile-menu.is-open');
    if (!anyOpen && overlay) {
      overlay.classList.remove('is-visible');
      document.body.style.overflow = '';
    }
  }

  function closeAllDrawers() {
    closeDrawer('cart-drawer');
    closeDrawer('search-drawer');
    closeDrawer('mobile-menu');
    const overlay = $('#overlay');
    if (overlay) overlay.classList.remove('is-visible');
    document.body.style.overflow = '';
  }

  /* ---- Cart drawer triggers ---- */
  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-cart-open]')) {
      e.preventDefault();
      openDrawer('cart-drawer');
      refreshCartDrawer();
    }
    if (e.target.closest('[data-cart-close]')) {
      e.preventDefault();
      closeDrawer('cart-drawer');
    }
    if (e.target.closest('[data-search-open]')) {
      e.preventDefault();
      openDrawer('search-drawer');
      const searchInput = $('.search-drawer__input');
      if (searchInput) setTimeout(() => searchInput.focus(), 300);
    }
    if (e.target.closest('[data-search-close]')) {
      e.preventDefault();
      closeDrawer('search-drawer');
    }
    if (e.target.closest('[data-mobile-menu-open]')) {
      e.preventDefault();
      openDrawer('mobile-menu');
    }
    if (e.target.closest('[data-mobile-menu-close]')) {
      e.preventDefault();
      closeDrawer('mobile-menu');
    }
    if (e.target.closest('#overlay')) {
      closeAllDrawers();
    }
  });

  /* ---- Escape key closes drawers ---- */
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeAllDrawers();
    }
  });

  /* ---- AJAX Cart ---- */
  async function fetchCart() {
    try {
      const response = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      return await response.json();
    } catch (err) {
      console.error('Failed to fetch cart:', err);
      return null;
    }
  }

  async function addToCart(variantId, quantity = 1) {
    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ id: variantId, quantity: quantity })
      });
      return await response.json();
    } catch (err) {
      console.error('Failed to add to cart:', err);
      return null;
    }
  }

  async function changeCart(line, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ line: line, quantity: quantity })
      });
      return await response.json();
    } catch (err) {
      console.error('Failed to update cart:', err);
      return null;
    }
  }

  function renderCartDrawer(cart) {
    const body = $('#cart-drawer-body');
    const footer = $('#cart-drawer-footer');
    const subtotalEl = $('#cart-subtotal');
    const cartCount = $('.site-header__cart-count');

    if (!body) return;

    if (!cart || cart.item_count === 0) {
      body.innerHTML = `
        <div class="cart-drawer__empty">
          <p class="cart-drawer__empty-text">Your cart is empty.</p>
          <a href="/collections/all" class="btn btn--outline" data-cart-close>Continue Shopping</a>
        </div>
      `;
      if (footer) footer.style.display = 'none';
      if (cartCount) cartCount.textContent = '0';
      return;
    }

    let html = '';
    cart.items.forEach((item, index) => {
      html += `
        <div class="cart-drawer__item" data-line="${index + 1}">
          <div class="cart-drawer__item-img">
            ${item.image ? `<img src="${item.image.replace('.jpg', '_120x.jpg')}" alt="${item.title}" loading="lazy">` : ''}
          </div>
          <div>
            <a href="${item.url}" class="cart-drawer__item-title">${item.product_title}</a>
            ${item.variant_title ? `<div class="cart-drawer__item-variant">${item.variant_title}</div>` : ''}
            <div class="cart-drawer__item-price">${formatMoney(item.price)} &times; ${item.quantity}</div>
          </div>
          <button class="cart-drawer__item-remove" data-cart-remove="${index + 1}">Remove</button>
        </div>
      `;
    });
    body.innerHTML = html;

    if (footer) {
      footer.style.display = 'flex';
      if (subtotalEl) subtotalEl.textContent = formatMoney(cart.total_price);
    }

    if (cartCount) cartCount.textContent = cart.item_count;
  }

  async function refreshCartDrawer() {
    const cart = await fetchCart();
    if (cart) renderCartDrawer(cart);
  }

  // Handle remove from cart drawer
  document.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('[data-cart-remove]');
    if (removeBtn) {
      e.preventDefault();
      const line = parseInt(removeBtn.dataset.cartRemove);
      await changeCart(line, 0);
      await refreshCartDrawer();
    }
  });

  /* ---- Product page: variant selection ---- */
  const productForm = $('#product-form');
  if (productForm) {
    const variantData = JSON.parse(productForm.dataset.variants || '[]');
    const optionSelects = $$('.product__option-value', productForm);
    const addToCartBtn = $('#add-to-cart-btn', productForm);
    const variantIdInput = $('#variant-id', productForm);
    const priceEl = $('.product__price-current');
    const compareEl = $('.product__price-compare');

    function getSelectedOptions() {
      return optionSelects
        .filter(s => s.classList.contains('product__option-value--selected'))
        .map(s => s.dataset.value);
    }

    function findVariant(selectedOptions) {
      return variantData.find(v => {
        return v.options.every((opt, i) => opt === selectedOptions[i]);
      });
    }

    function updateVariant() {
      const selected = getSelectedOptions();
      const variant = findVariant(selected);

      if (variant) {
        variantIdInput.value = variant.id;
        if (priceEl) priceEl.textContent = formatMoney(variant.price);
        if (compareEl) {
          if (variant.compare_at_price && variant.compare_at_price > variant.price) {
            compareEl.textContent = formatMoney(variant.compare_at_price);
            compareEl.style.display = '';
          } else {
            compareEl.style.display = 'none';
          }
        }
        if (addToCartBtn) {
          if (variant.available) {
            addToCartBtn.disabled = false;
            addToCartBtn.textContent = 'Add to Cart';
          } else {
            addToCartBtn.disabled = true;
            addToCartBtn.textContent = 'Sold Out';
          }
        }
        // Update URL
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', newUrl);
      }

      // Color variant image switching
      const thumbs = $$('.product__thumb');
      if (thumbs.length > 0) {
        // Get the selected color (first option)
        const selectedColor = selected[0];
        // Check if any thumbnails have data-color attributes
        const colorThumbs = Array.from(thumbs).filter(t => t.dataset.color);
        // Only filter if there are color-tagged thumbnails and more than one color
        if (colorThumbs.length > 0 && selectedColor) {
          const matching = colorThumbs.filter(t => t.dataset.color === selectedColor);
          // If we found matching images, filter; otherwise show all
          if (matching.length > 0) {
            thumbs.forEach(t => {
              if (t.dataset.color === selectedColor) {
                t.style.display = '';
              } else {
                t.style.display = 'none';
                t.classList.remove('product__thumb--active');
              }
            });
            // Switch main image to first matching thumbnail
            const firstMatch = matching[0];
            if (firstMatch && firstMatch.dataset.src) {
              const mainImg = $('.product__media-main img');
              if (mainImg) mainImg.src = firstMatch.dataset.src;
              thumbs.forEach(t => t.classList.remove('product__thumb--active'));
              firstMatch.classList.add('product__thumb--active');
            }
          }
        }
      }
    }

    // Option click handlers
    optionSelects.forEach(select => {
      select.addEventListener('click', (e) => {
        e.preventDefault();
        const group = select.closest('.product__options');
        if (group) {
          $$('.product__option-value', group).forEach(s => s.classList.remove('product__option-value--selected'));
        }
        select.classList.add('product__option-value--selected');
        updateVariant();
      });
    });

    // Add to cart
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        const variantId = variantIdInput.value;
        const qtyInput = $('#product-qty');
        const quantity = qtyInput ? parseInt(qtyInput.value) || 1 : 1;

        if (!variantId) return;

        addToCartBtn.disabled = true;
        addToCartBtn.textContent = 'Adding...';

        await addToCart(variantId, quantity);
        await refreshCartDrawer();
        openDrawer('cart-drawer');

        addToCartBtn.disabled = false;
        addToCartBtn.textContent = 'Add to Cart';
      });
    }

    // Quantity controls
    const qtyInput = $('#product-qty');
    if (qtyInput) {
      const qtyDec = $('.product__qty-btn--dec');
      const qtyInc = $('.product__qty-btn--inc');
      if (qtyDec) {
        qtyDec.addEventListener('click', () => {
          const val = parseInt(qtyInput.value) || 1;
          if (val > 1) qtyInput.value = val - 1;
        });
      }
      if (qtyInc) {
        qtyInc.addEventListener('click', () => {
          const val = parseInt(qtyInput.value) || 1;
          qtyInput.value = val + 1;
        });
      }
    }

    // Thumbnail switching
    $$('.product__thumb').forEach(thumb => {
      thumb.addEventListener('click', (e) => {
        e.preventDefault();
        $$('.product__thumb').forEach(t => t.classList.remove('product__thumb--active'));
        thumb.classList.add('product__thumb--active');
        const mainImg = $('.product__media-main img');
        if (mainImg && thumb.dataset.src) {
          mainImg.src = thumb.dataset.src;
        }
      });
    });

    // Initialize
    updateVariant();
  }

  /* ---- Cart page: quantity update ---- */
  $$('.cart-item__qty-btn').forEach(btn => {
    btn.addEventListener('click', async (e) => {
      e.preventDefault();
      const line = parseInt(btn.dataset.line);
      const qtyInput = $(`#cart-qty-${line}`);
      const currentVal = parseInt(qtyInput.value) || 1;
      const isDec = btn.classList.contains('cart-item__qty-btn--dec');
      const newVal = isDec ? Math.max(0, currentVal - 1) : currentVal + 1;
      qtyInput.value = newVal;
      // Submit the form to update cart
      const form = $('#cart-form');
      if (form) form.submit();
    });
  });

  /* ---- Search drawer: live search ---- */
  const searchInput = $('.search-drawer__input');
  let searchTimeout;
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      const query = e.target.value.trim();
      const resultsEl = $('#search-results');
      clearTimeout(searchTimeout);
      if (query.length < 2) {
        if (resultsEl) resultsEl.innerHTML = '';
        return;
      }
      searchTimeout = setTimeout(async () => {
        try {
          const response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`);
          const data = await response.json();
          const products = data.resources?.results?.products || [];
          const exactTitleMatches = products.filter((product) =>
            product.title.toLowerCase().includes(query.toLowerCase())
          );
          const displayedProducts = exactTitleMatches.length > 0 ? exactTitleMatches : products;
          if (resultsEl) {
            if (displayedProducts.length === 0) {
              resultsEl.innerHTML = '<p style="color:var(--text-muted);text-align:center;padding:24px;">No results found</p>';
            } else {
              resultsEl.innerHTML = displayedProducts.map(p => `
                <a href="${p.url}" class="search-drawer__result">
                  ${p.image ? `<div class="search-drawer__result-img"><img src="${p.image.replace('.jpg', '_112x.jpg')}" alt="${p.title}" loading="lazy"></div>` : ''}
                  <div>
                    <div class="search-drawer__result-title">${p.title}</div>
                    <div class="search-drawer__result-price">${p.price}</div>
                  </div>
                </a>
              `).join('');
            }
          }
        } catch (err) {
          console.error('Search error:', err);
        }
      }, 300);
    });
  }

  /* ---- Product page accordion (handled by inline script in main-product.liquid via event delegation) ---- */

  /* ---- Initialize cart count on page load ---- */
  (async function initCartCount() {
    const cart = await fetchCart();
    if (cart) {
      const cartCount = $('.site-header__cart-count');
      if (cartCount) cartCount.textContent = cart.item_count;
    }
  })();

})();
